/**
 * Botões compartilhados de integração Snov.io para qualquer view (Lead, Opp, etc.)
 * - SnovEnrichButton: enriquece contatos existentes que não têm email
 * - SnovSuggestButton: descobre candidatos do ICP via IA + valida com Snov
 */
import { useState } from 'react'
import { Search, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import {
  useSnovEnrichCompany,
  useSnovSuggestContacts,
  useBulkCreateContacts,
  useApplySnovSuggestion,
  type SnovSuggestion,
  type SnovEnrichDetail,
} from '@/hooks/useLeads'

// ──────────────────────────────────────────────────────────────────────────────
// SnovEnrichButton
// ──────────────────────────────────────────────────────────────────────────────

export function SnovEnrichButton({
  companyId,
  onEnriched,
}: {
  companyId: string
  onEnriched?: () => void
}) {
  const enrichMut = useSnovEnrichCompany()
  const applyMut = useApplySnovSuggestion()
  const [result, setResult] = useState<null | {
    ok: boolean
    error?: string
    domain?: string
    domain_count?: number
    credits?: number | null
    autoFilled: SnovEnrichDetail[]
    suggestions: SnovEnrichDetail[]
  }>(null)
  const [appliedIds, setAppliedIds] = useState<Set<string>>(new Set())

  const handleClick = async () => {
    if (!companyId) return
    setResult(null); setAppliedIds(new Set())
    try {
      const data = await enrichMut.mutateAsync(companyId)
      setResult({
        ok: true,
        domain: data.domain,
        domain_count: data.domain_emails_in_snov,
        credits: data.credits_balance,
        autoFilled: data.auto_filled || [],
        suggestions: data.suggestions_to_review || [],
      })
      onEnriched?.()
    } catch (e: any) {
      setResult({ ok: false, error: e?.response?.data?.detail || 'Erro ao enriquecer com Snov.io', autoFilled: [], suggestions: [] })
    }
  }

  const applyOne = async (s: SnovEnrichDetail) => {
    try {
      await applyMut.mutateAsync({ companyId, contactId: s.contact_id, email: s.suggested_email })
      setAppliedIds(prev => { const n = new Set(prev); n.add(s.contact_id); return n })
      onEnriched?.()
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Erro ao aplicar email')
    }
  }

  return (
    <div className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={handleClick}
        disabled={enrichMut.isPending}
        title="Buscar emails dos contatos via Snov.io"
      >
        <Search className="h-4 w-4 mr-1" />
        {enrichMut.isPending ? 'Enriquecendo...' : 'Enriquecer com Snovio'}
      </Button>
      {result && (
        <div className={`absolute right-0 top-full mt-2 w-[28rem] z-50 p-3 rounded-lg shadow-lg border text-xs ${result.ok ? 'bg-card border-border' : 'bg-destructive/10 border-destructive/40'}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <span className="font-semibold text-sm">{result.ok ? '✓ Snov.io' : '✗ Snov.io'}</span>
            <button onClick={() => setResult(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3 w-3" /></button>
          </div>

          {!result.ok && <p className="text-destructive">{result.error}</p>}

          {result.ok && (
            <>
              <p className="text-muted-foreground mb-2">
                Domínio <b>{result.domain}</b> tem {result.domain_count} emails na base Snov. Créditos restantes: {result.credits ?? '—'}
              </p>

              {result.autoFilled.length > 0 && (
                <div className="mb-3">
                  <div className="font-semibold text-green-600 dark:text-green-400 mb-1">
                    ✓ {result.autoFilled.length} preenchido(s) automaticamente:
                  </div>
                  <ul className="space-y-0.5">
                    {result.autoFilled.map((d, i) => (
                      <li key={i} className="font-mono text-[11px] truncate">{d.name} → {d.suggested_email}</li>
                    ))}
                  </ul>
                </div>
              )}

              {result.suggestions.length > 0 && (
                <div className="mb-2">
                  <div className="font-semibold text-amber-600 dark:text-amber-400 mb-1">
                    💡 {result.suggestions.length} sugestão(ões) — Snov achou email corporativo, mas o contato tem email pessoal:
                  </div>
                  <ul className="space-y-1.5">
                    {result.suggestions.map((s, i) => {
                      const applied = appliedIds.has(s.contact_id)
                      return (
                        <li key={i} className="border rounded p-1.5">
                          <div className="font-medium text-[11px] truncate">{s.name}</div>
                          <div className="font-mono text-[10px] text-muted-foreground line-through truncate">{s.current_email}</div>
                          <div className="font-mono text-[10px] text-green-600 dark:text-green-400 truncate">→ {s.suggested_email}</div>
                          {s.variation_used && (
                            <div className="text-[9px] text-muted-foreground italic">snov match: "{s.variation_used}"</div>
                          )}
                          <div className="flex justify-end mt-1">
                            {applied ? (
                              <span className="text-[10px] text-green-600 dark:text-green-400">✓ Aplicado</span>
                            ) : (
                              <button
                                onClick={() => applyOne(s)}
                                disabled={applyMut.isPending}
                                className="px-2 py-0.5 text-[10px] rounded bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
                              >
                                Aplicar
                              </button>
                            )}
                          </div>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              )}

              {result.autoFilled.length === 0 && result.suggestions.length === 0 && (
                <p className="text-muted-foreground italic">
                  Nenhum email novo encontrado para os contatos atuais. Pode ser que (a) os contatos já têm email corporativo correto, ou (b) Snov não tem registro destes nomes no domínio.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// SnovSuggestButton — descobre candidatos do ICP via IA + valida emails na Snov
// ──────────────────────────────────────────────────────────────────────────────

export function SnovSuggestButton({
  companyId,
  onCreated,
}: {
  companyId: string
  onCreated?: () => void
}) {
  const suggestMut = useSnovSuggestContacts()
  const bulkCreateMut = useBulkCreateContacts()
  const [open, setOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<SnovSuggestion[] | null>(null)
  const keyOf = (s: SnovSuggestion) => s.email || `name|${s.first_name}|${s.last_name}|${s.role}`
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [error, setError] = useState('')
  const [feedback, setFeedback] = useState('')

  const openModal = async () => {
    if (!companyId) return
    setError(''); setFeedback(''); setSelectedKeys(new Set())
    setSuggestions(null)
    setOpen(true)
    try {
      const res = await suggestMut.mutateAsync(companyId)
      setSuggestions(res.suggestions || [])
      const pre = new Set((res.suggestions || []).filter(s => !s.already_exists && s.email).map(keyOf))
      setSelectedKeys(pre)
      if (!res.suggestions || res.suggestions.length === 0) {
        setFeedback(res.message || `Nenhum candidato encontrado para ${res.domain}. Pode ser que (a) a empresa tem pouca exposição pública pros perfis do ICP, ou (b) o Claude não conseguiu identificar nomes confiáveis na pesquisa.`)
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao buscar sugestões')
      setSuggestions([])  // mantém modal aberto com erro
    }
  }

  const close = () => { setOpen(false); setSuggestions(null); setError(''); setFeedback(''); setSelectedKeys(new Set()) }

  const toggle = (s: SnovSuggestion) => {
    const k = keyOf(s)
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k); else next.add(k)
      return next
    })
  }

  const cadastrar = async () => {
    if (!suggestions || selectedKeys.size === 0) return
    const toCreate = suggestions
      .filter(s => selectedKeys.has(keyOf(s)) && !s.already_exists)
      .map(s => ({ first_name: s.first_name, last_name: s.last_name, email: s.email, job_title: s.role }))
    if (toCreate.length === 0) { setFeedback('Nada novo pra cadastrar.'); return }
    try {
      const result = await bulkCreateMut.mutateAsync({ companyId, contacts: toCreate })
      setFeedback(`✓ ${result.created} contato(s) criado(s)${result.skipped_duplicates > 0 ? `, ${result.skipped_duplicates} duplicado(s)` : ''}.`)
      onCreated?.()
      setTimeout(close, 1500)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Erro ao criar contatos')
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={openModal} disabled={suggestMut.isPending} title="Sugerir contatos do ICP via IA + Snov">
        <Search className="h-4 w-4 mr-1" />
        {suggestMut.isPending ? 'Buscando...' : 'Sugerir Contatos'}
      </Button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={close}>
          <div className="bg-card border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <div>
                <h2 className="font-semibold">Sugestões de Contatos do ICP</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Diretores, Gestores, C-Level, Compliance, Segurança, IAM, GRC</p>
              </div>
              <button onClick={close} className="p-1.5 rounded hover:bg-accent"><X className="h-4 w-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {suggestMut.isPending ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                  Pesquisando candidatos via IA + validando emails na Snov...
                  <p className="text-xs mt-2">(pode levar 30-90s)</p>
                </div>
              ) : error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : suggestions && suggestions.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground mb-2">
                    <span className="text-green-600 dark:text-green-400 font-semibold">Verde</span> = email confirmado pela Snov; <span className="text-amber-600 dark:text-amber-400 font-semibold">Amarelo</span> = só nome/cargo (preencha email depois).
                  </p>
                  {suggestions.map((s, i) => {
                    const isSelected = selectedKeys.has(keyOf(s))
                    const hasEmail = !!s.email
                    return (
                      <label
                        key={i}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                          s.already_exists ? 'bg-muted/40 opacity-60' :
                          hasEmail ? 'border-green-500/30 hover:bg-green-50/30 dark:hover:bg-green-950/20' :
                          'border-amber-500/30 hover:bg-amber-50/30 dark:hover:bg-amber-950/20'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(s)}
                          disabled={s.already_exists}
                          className="h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{s.first_name} {s.last_name}</div>
                          <div className="text-xs text-muted-foreground truncate">{s.role}</div>
                          <div className={`text-xs font-mono mt-0.5 ${hasEmail ? '' : 'italic text-muted-foreground'}`}>
                            {hasEmail ? s.email : '— sem email validado pela Snov —'}
                          </div>
                        </div>
                        {s.already_exists && <Badge variant="outline" className="text-[9px]">Já cadastrado</Badge>}
                        {!s.already_exists && hasEmail && <Badge variant="outline" className="text-[9px] border-green-500/40 text-green-600 dark:text-green-400">Snov ✓</Badge>}
                        {!s.already_exists && !hasEmail && <Badge variant="outline" className="text-[9px] border-amber-500/40 text-amber-600 dark:text-amber-400">só IA</Badge>}
                      </label>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">{feedback || 'Nenhuma sugestão encontrada.'}</p>
              )}
            </div>

            {suggestions && suggestions.length > 0 && (
              <div className="px-5 py-3 border-t flex items-center justify-between">
                <span className="text-xs text-muted-foreground">{selectedKeys.size} selecionado(s)</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={close}>Cancelar</Button>
                  <Button size="sm" onClick={cadastrar} disabled={bulkCreateMut.isPending || selectedKeys.size === 0}>
                    {bulkCreateMut.isPending ? 'Cadastrando...' : `Cadastrar ${selectedKeys.size}`}
                  </Button>
                </div>
              </div>
            )}
            {feedback && <p className="px-5 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30 border-t">{feedback}</p>}
          </div>
        </div>
      )}
    </>
  )
}
